import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { PersonaFisicaModel } from "../../../models/PersonaFisicaModel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { displayFormatted } from "../../../utils/date";
import PersonaFisicaFormComponent from "./PersonaFisicaFormComponent";
import CustomerEmailsTable from "../CustomerEmailsTable";
import CustomerTelefoniTable from "../CustomerTelefoniTable";
import CustomerIndirizziTable from "../CustomerIndirizziTable";
import CustomerDocumentTable from "../CustomerDocumentTable";
import BankAccountTable from "../BankAccountTable";
import CustomerCredenzialiPortaleVenditaTable from "../CustomerCredenzialiPortaleVenditaTable";
import BeneTableComponent from "../../bene/BeneTableComponent";
import ContrattiTableComponent from "../../contratto/ContrattiTableComponent";
import { CredenzialiPortaleVenditaModel } from "../../../models/CredenzialiPortaleVenditaModel";
import { Indirizzo } from "../../../models/IndirizzoModel";
import { CustomerDocument } from "../../../models/api/customer/document/customer-document-model";
import { BankAccount } from "../../../models/api/bank-account/bank-account-model";
import { useQuery } from "react-query";
import { fetchSelectBeni, fetchBeniStats } from "../../../api/beni";
import { fetchSelectContratti } from "../../../api/contratto";
import { fetchBeniQueryKey, fetchContractsQueryKey } from "../../../utils/QueryClient";
import { BeneModelDetailed } from "../../../models/BeneModel";
import { ContrattoConStatoTipologia } from "../../../models/ContrattoModel";
import { FetchPaginatedApiModel } from "../../../models/shared/CommonModels";
import ExcelExportButton from "../../shared/ExcelExportButton";
import { GridColDef } from "@mui/x-data-grid";

interface WithPF {
  pef: PersonaFisicaModel;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const InfoRow: React.FC<{ label: string; value: string | undefined }> = ({
  label,
  value,
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1">{value || "-"}</Typography>
  </Box>
);

const ClienteNewDetailComponent: React.FC<WithPF> = ({ pef }) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(4);

  // State per i form modals
  const [mainDataEditFormOpen, setMainDataEditFormOpen] = useState(false);
  const [emailDataCreateFormOpen, setEmailDataCreateFormOpen] = useState(false);
  const [telefonoDataCreateFormOpen, setTelefonoDataCreateFormOpen] = useState(false);
  const [indirizziDataCreateFormOpen, setIndirizziDataCreateFormOpen] = useState(false);
  const [documentCreateFormOpen, setDocumentCreateFormOpen] = useState(false);
  const [bankAccountCreateFormOpen, setBankAccountCreateFormOpen] = useState(false);
  const [enableCreateButton, setEnableCreateButton] = useState(true);
  const [portaliVenditaDataCreateFormOpen, setPortaliVenditaDataCreateFormOpen] = useState(false);
  const [beniDataCreateFormOpen, setBeniDataCreateFormOpen] = useState(false);
  const [contrattiDataCreateFormOpen, setContrattiDataCreateFormOpen] = useState(false);

  // Query per le statistiche dei beni
  const [beniStats, setBeniStats] = useState<{
    totale_beni: number;
    beni_in_valutazione: number;
    beni_approvati: number;
    beni_venduti: number;
  }>({ totale_beni: 0, beni_in_valutazione: 0, beni_approvati: 0, beni_venduti: 0 });

  useQuery({
    queryKey: ['beniStats', pef.id_cliente],
    queryFn: () => fetchBeniStats(pef.id_cliente),
    onSuccess: (getResponse) => {
      setBeniStats(getResponse.data);
    },
    onError: (error) => {
      console.error('Errore nel caricamento delle statistiche beni:', error);
    },
    refetchOnWindowFocus: false,
  });

  // Query per i beni del cliente
  const [beni, setBeni] = useState<FetchPaginatedApiModel<BeneModelDetailed[]>>(undefined as any);
  
  useQuery({
    queryKey: [fetchBeniQueryKey, pef.id_cliente],
    queryFn: () => fetchSelectBeni(0, undefined, { id: 0, field: "id", direction: "asc" }, pef.id_cliente),
    onSuccess: (getResponse) => {
      console.log("Beni caricati:", getResponse.data);
      setBeni(getResponse.data);
    },
    refetchOnWindowFocus: false,
  });

  // Query per i contratti del cliente
  const [contratti, setContratti] = useState<FetchPaginatedApiModel<ContrattoConStatoTipologia[]>>(undefined as any);
  
  useQuery({
    queryKey: [fetchContractsQueryKey, pef.id_cliente],
    queryFn: () => fetchSelectContratti(0, undefined, { id: 0, field: "id", direction: "asc" }, pef.id_cliente),
    onSuccess: (getResponse) => {
      setContratti(getResponse.data);
    },
    refetchOnWindowFocus: false,
  });

  // Definizione colonne per export Excel
  const beniExcelColumns: GridColDef[] = [
    { field: "id", headerName: "ID Bene", width: 80 },
    { field: "ubicazione", headerName: "Ubicazione", width: 150 },
    { field: "sotto_tipologia", headerName: "Sotto-Categoria", width: 150 },
    { field: "qualita", headerName: "Stato", width: 120 },
    { field: "brand", headerName: "Brand", width: 150 },
    { field: "contratto", headerName: "Contratto", width: 200 },
    { field: "condizione", headerName: "Condizione", width: 150 },
    { field: "valutazione_min", headerName: "Min Valutazione", width: 120 },
    { field: "valutazione_max", headerName: "Max Valutazione", width: 120 },
    { field: "data_vendita", headerName: "Data Vendita", width: 120 },
    { field: "prezzo_vendita", headerName: "Prz. Vend.", width: 100 },
    { field: "prezzo_netto", headerName: "Prz. Netto", width: 100 },
    { field: "note_vendita", headerName: "Note di Vendita", width: 200 },
  ];

  const beniExcelRows = beni?.data?.map(bene => ({
    id: bene.id,
    ubicazione: bene.ubicazionebene?.nome || "-",
    sotto_tipologia: bene.sottotipologiabene?.nome || "-",
    qualita: bene.qualitabene?.nome || "-",
    brand: bene.brandbene?.nome || "-",
    contratto: bene.benecontratto?.[0]?.contratto?.titolo || "-",
    condizione: bene.benecontratto?.[0]?.condizione?.nome || "-",
    valutazione_min: bene.valutazione_minima + "€",
    valutazione_max: bene.valutazione_massima + "€",
    data_vendita: bene.benecontratto?.[0]?.venditabenecontratto?.data_vendita 
      ? new Date(bene.benecontratto[0].venditabenecontratto.data_vendita).toLocaleDateString("it-IT")
      : "-",
    prezzo_vendita: bene.benecontratto?.[0]?.venditabenecontratto?.totale_lordo !== undefined
      ? bene.benecontratto[0].venditabenecontratto.totale_lordo + "€"
      : "-",
    prezzo_netto: bene.benecontratto?.[0]?.venditabenecontratto?.totale_netto !== undefined
      ? bene.benecontratto[0].venditabenecontratto.totale_netto + "€"
      : "-",
    note_vendita: bene.benecontratto?.[0]?.venditabenecontratto?.selling_notes || "-",
  })) || [];

  // Definizione colonne per export Excel contratti
  const contrattiExcelColumns: GridColDef[] = [
    { field: "id", headerName: "ID Contratto", width: 110 },
    { field: "cognome", headerName: "Cognome", width: 150 },
    { field: "nome", headerName: "Nome", width: 150 },
    { field: "titolo", headerName: "Titolo", width: 200 },
    { field: "stato", headerName: "Stato del Contratto", width: 150 },
    { field: "tipologia", headerName: "Tipologia del Contratto", width: 180 },
  ];

  const contrattiExcelRows = contratti?.data?.map(contratto => ({
    id: contratto.id,
    cognome: pef.cognome,
    nome: pef.nome,
    titolo: contratto.titolo || "-",
    stato: contratto.statocontratto?.nome || "-",
    tipologia: contratto.tipologiacontratto?.nome || "-",
  })) || [];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Usa le metriche dalle statistiche
  const totaleBeni = beniStats.totale_beni;
  const beniInValutazione = beniStats.beni_in_valutazione;
  const beniApprovati = beniStats.beni_approvati;
  const beniVenduti = beniStats.beni_venduti;

  return (
    <>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent
          sx={{
            background: "linear-gradient(90deg, #fff9c4 0%, #fdd835 100%)",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/personafisica")} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontSize: "2.125rem", mb: 0.5 }}>
                <strong>{pef.cognome} {pef.nome}</strong>
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {(() => {
                  const preferredAddress = pef.anagraficacliente.indirizzi?.find(i => i.preferred === true || i.preferred === 1);
                  return preferredAddress
                    ? `${preferredAddress.indirizzo} - ${preferredAddress.nazione}`
                    : pef.codice_fiscale;
                })()}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`Totale Beni: ${totaleBeni}`}
                size="medium"
                sx={{
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={`Beni in Valutazione: ${beniInValutazione}`}
                size="medium"
                sx={{
                  bgcolor: "#fff3e0",
                  color: "#f57c00",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={`Beni Approvati: ${beniApprovati}`}
                size="medium"
                sx={{
                  bgcolor: "#e8f5e9",
                  color: "#388e3c",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={`Beni Venduti: ${beniVenduti}`}
                size="medium"
                sx={{
                  bgcolor: "#f3e5f5",
                  color: "#7b1fa2",
                  fontWeight: 600,
                }}
              />
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Informazioni Principali" />
          <Tab label="Contatti" />
          <Tab label="Documenti e Conti" />
          <Tab label="Portali Vendita" />
          <Tab label="BENI" />
          <Tab label="CONTRATTI" />
        </Tabs>
      </Paper>

      {/* Tab 0: Informazioni Principali */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Dati Anagrafici */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ p: 2, "& > *:last-child": { mb: 0 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" color="primary" fontWeight={600}>
                  Dati Anagrafici
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setMainDataEditFormOpen(true)}
                >
                  Modifica
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Cognome" value={pef.cognome} />
              <InfoRow label="Nome" value={pef.nome} />
              <InfoRow label="Codice Fiscale" value={pef.codice_fiscale} />
              <InfoRow label="Genere" value={pef.gender} />
            </Card>
          </Grid>

          {/* Nascita */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ p: 2, "& > *:last-child": { mb: 0 } }}>
              <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
                Nascita
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Data di Nascita" value={displayFormatted(pef.data_di_nascita)} />
              <InfoRow label="Luogo di Nascita" value={pef.birth_place} />
            </Card>
          </Grid>

          {/* Genesi Cliente */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ p: 2, "& > *:last-child": { mb: 0 } }}>
              <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
                Origine Cliente
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Genesi Cliente" value={pef.anagraficacliente.genesi_cliente} />
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 1: Contatti */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Email */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" color="primary" fontWeight={600}>Email</Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setEmailDataCreateFormOpen(!emailDataCreateFormOpen)}
                >
                  Aggiungi
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <CustomerEmailsTable
                anagrafica_cliente_id={pef.id_cliente}
                createFormOpen={emailDataCreateFormOpen}
                toggleCreateFormOpen={() => setEmailDataCreateFormOpen(!emailDataCreateFormOpen)}
              />
            </Card>
          </Grid>

          {/* Telefoni */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" color="primary" fontWeight={600}>Telefoni</Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setTelefonoDataCreateFormOpen(!telefonoDataCreateFormOpen)}
                >
                  Aggiungi
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <CustomerTelefoniTable
                anagrafica_cliente_id={pef.id_cliente}
                createFormOpen={telefonoDataCreateFormOpen}
                toggleCreateFormOpen={() => setTelefonoDataCreateFormOpen(!telefonoDataCreateFormOpen)}
              />
            </Card>
          </Grid>

          {/* Indirizzi - full width */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" color="primary" fontWeight={600}>Indirizzi</Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setIndirizziDataCreateFormOpen(!indirizziDataCreateFormOpen)}
                >
                  Aggiungi
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <CustomerIndirizziTable
                anagrafica_cliente_id={pef.id_cliente}
                record={{} as Indirizzo}
                createFormOpen={indirizziDataCreateFormOpen}
                toggleCreateFormOpen={() => setIndirizziDataCreateFormOpen(!indirizziDataCreateFormOpen)}
              />
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Documenti e Conti */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Documenti */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" color="primary" fontWeight={600}>Documenti</Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setDocumentCreateFormOpen(!documentCreateFormOpen)}
                >
                  Aggiungi
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <CustomerDocumentTable
                anagrafica_cliente_id={pef.id_cliente}
                record={{} as CustomerDocument}
                createFormOpen={documentCreateFormOpen}
                toggleCreateFormOpen={() => setDocumentCreateFormOpen(!documentCreateFormOpen)}
              />
            </Card>
          </Grid>

          {/* Conti Bancari */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" color="primary" fontWeight={600}>Conti Bancari</Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setBankAccountCreateFormOpen(!bankAccountCreateFormOpen)}
                >
                  Aggiungi
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <BankAccountTable
                anagrafica_cliente_id={pef.id_cliente}
                record={{} as BankAccount}
                createFormOpen={bankAccountCreateFormOpen}
                toggleCreateFormOpen={() => setBankAccountCreateFormOpen(!bankAccountCreateFormOpen)}
              />
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 3: Portali Vendita */}
      <TabPanel value={tabValue} index={3}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" color="primary" fontWeight={600}>Credenziali Portali Vendita</Typography>
            <Button
              disabled={!enableCreateButton}
              variant="contained"
              color="success"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setPortaliVenditaDataCreateFormOpen(!portaliVenditaDataCreateFormOpen)}
            >
              Aggiungi
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <CustomerCredenzialiPortaleVenditaTable
            anagrafica_cliente_id={pef.id_cliente}
            record={{} as CredenzialiPortaleVenditaModel}
            createFormOpen={portaliVenditaDataCreateFormOpen}
            toggleCreateFormOpen={() => setPortaliVenditaDataCreateFormOpen(!portaliVenditaDataCreateFormOpen)}
            setEnableCreateButton={setEnableCreateButton}
          />
        </Card>
      </TabPanel>

      {/* Tab 4: BENI */}
      <TabPanel value={tabValue} index={4}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>Beni Associati</Typography>
          <Divider sx={{ mb: 2 }} />
          <BeneTableComponent
            alterFabVisibility={() => setBeniDataCreateFormOpen(!beniDataCreateFormOpen)}
            fabVisible={false}
            enableCreate={beniDataCreateFormOpen}
            dettaglio={false}
            anagrafica_cliente_id={pef.id_cliente}
            toolbarActions={
              <>
                <ExcelExportButton 
                  columns={beniExcelColumns}
                  rows={beniExcelRows}
                />
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setBeniDataCreateFormOpen(!beniDataCreateFormOpen)}
                >
                  Aggiungi
                </Button>
              </>
            }
          />
        </Card>
      </TabPanel>

      {/* Tab 5: CONTRATTI */}
      <TabPanel value={tabValue} index={5}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
            Contratti Associati
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ContrattiTableComponent
            alterFabVisibility={() => setContrattiDataCreateFormOpen(!contrattiDataCreateFormOpen)}
            fabVisible={false}
            enableCreate={contrattiDataCreateFormOpen}
            anagrafica_cliente_id={pef.id_cliente}
            toolbarActions={
              <>
                <ExcelExportButton 
                  columns={contrattiExcelColumns}
                  rows={contrattiExcelRows}
                />
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setContrattiDataCreateFormOpen(!contrattiDataCreateFormOpen)}
                >
                  Aggiungi
                </Button>
              </>
            }
          />
        </Card>
      </TabPanel>

      {/* Edit Form Modal */}
      <PersonaFisicaFormComponent
        initialData={pef}
        open={mainDataEditFormOpen}
        toggleOpen={() => setMainDataEditFormOpen(!mainDataEditFormOpen)}
        editEnabled={true}
      />
    </>
  );
};

export default ClienteNewDetailComponent;
